// Copyright (c) Microsoft. All rights reserved.

import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components'; // tokens eklendi
import React, { HTMLAttributes } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { IChatMessage } from '../../../libs/models/ChatMessage';
import * as utils from './../../utils/TextUtils';
import { useAppSelector } from '../../../redux/app/hooks';
import { RootState } from '../../../redux/app/store';
import { FeatureKeys } from '../../../redux/features/app/AppState';
import { customTokens } from '../../../styles';

const useClasses = makeStyles({
    content: {
        wordBreak: 'break-word',
        '& p': {
            margin: '0',
        },
    },
    linkUserMessage: {
        color: customTokens.colorLinkUserMessage,
        textDecoration: 'underline',
        textDecorationColor: 'currentColor',
        textUnderlineOffset: '2px',
        transition: 'color 0.2s ease',
        cursor: 'pointer',
        fontStyle: 'italic',
        '&:hover': {
            color: customTokens.colorLinkUserMessageHover,
        },
        '&:active': {
            color: customTokens.colorLinkUserMessageActive,
        },
    },
    linkAssistantMessage: {
        color: customTokens.colorLinkAssistantMessage,
        textDecoration: 'underline',
        textDecorationColor: 'currentColor',
        textUnderlineOffset: '2px',
        transition: 'color 0.2s ease',
        cursor: 'pointer',
        fontStyle: 'italic',
        '&:hover': {
            color: customTokens.colorLinkAssistantMessageHover,
        },
        '&:active': {
            color: customTokens.colorLinkAssistantMessageActive,
        },
    },
    // --- Tablo Stilleri Başlangıcı ---
    tableContainer: {
        overflowX: 'auto', // Geniş tabloların mobilde taşmasını engeller
        marginTop: '10px',
        marginBottom: '10px',
        maxWidth: '900px',
    },
    table: {
        minWidth: '100%', // Tablonun minimum genişliği container genişliği kadar
        borderCollapse: 'collapse',
        fontSize: tokens.fontSizeBase300, // Fluent font boyutu
        border: `1px solid ${tokens.colorNeutralStroke1}`, // Kenarlık rengi (tema uyumlu)
    },
    thead: {
        backgroundColor: tokens.colorNeutralBackground2, // Başlık arka planı (hafif gri)
    },
    th: {
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
        fontWeight: tokens.fontWeightSemibold,
        textAlign: 'left',
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        color: tokens.colorNeutralForeground1,
        minWidth: '120px',
    },
    td: {
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    // --- Tablo Stilleri Bitişi ---
    code: {
        backgroundColor: tokens.colorNeutralBackground3,
        borderRadius: '4px',
    },
    pre: {
        backgroundColor: tokens.colorNeutralBackgroundInverted,
        color: tokens.colorNeutralForegroundInverted,
        padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
        borderRadius: '8px',
        overflowX: 'auto',
    },
    preDark: {
        backgroundColor: tokens.colorNeutralBackground3,
        color: tokens.colorNeutralForeground1,
    },
    svgContainer: {
        margin: `${tokens.spacingVerticalM} 0`,
        width: '100%',
        overflowX: 'auto',
    },
    // --- Blockquote Stilleri Başlangıcı ---
    blockquote: {
        lineHeight: 1.5,
        fontSize: '1.5em',
        margin: '24px auto',
        position: 'relative',
        padding: `${tokens.spacingVerticalM} ${tokens.spacingVerticalM} ${tokens.spacingVerticalM} ${tokens.spacingHorizontalXL}`,
        borderLeft: `solid 6px ${tokens.colorNeutralStroke2}`,
        '& p': {
            margin: '0 0 0 32px',
            fontSize: 'inherit',
        },
        '&::before': {
            left: '16px',
            top: '-8px',
            display: 'block',
            fontSize: '3em',
            content: '"\\201C"',
            position: 'absolute',
        },
    },
    // --- Blockquote Stilleri Bitişi ---
});

interface ChatHistoryTextContentProps {
    message: IChatMessage;
    isMe?: boolean;
}

export const ChatHistoryTextContent: React.FC<ChatHistoryTextContentProps> = ({ message, isMe = false }) => {
    const classes = useClasses();
    const { features } = useAppSelector((state: RootState) => state.app);
    const isDarkMode = features[FeatureKeys.DarkMode].enabled;
    const content = utils.replaceCitationLinksWithIndices(utils.formatChatTextContent(message.content), message);
    
    const linkClass = isMe ? classes.linkUserMessage : classes.linkAssistantMessage;

    return (
        <div className={classes.content}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{
                    // Linkler
                    a: ({ node: _node, ...props }) => (
                        <a 
                            {...props} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className={linkClass}
                        />
                    ),
                    // --- Tablo Override İşlemleri ---
                    table: ({ node: _node, ...props }) => (
                        <div className={classes.tableContainer}>
                            <table className={classes.table} {...props} />
                        </div>
                    ),
                    thead: ({ node: _node, ...props }) => <thead className={classes.thead} {...props} />,
                    th: ({ node: _node, ...props }) => <th className={classes.th} {...props} />,
                    td: ({ node: _node, ...props }) => <td className={classes.td} {...props} />,
                    // -------------------------------

                    // Blockquote
                    blockquote: ({ node: _node, ...props }) => <blockquote className={classes.blockquote} {...props} />,

                    // Kod Blokları
                    code({
                        node: _node,
                        inline,
                        className,
                        children,
                        ...props
                    }: HTMLAttributes<HTMLElement> & { inline?: boolean; node?: any }) {
                        const match = /language-(\w+)/.exec(className ?? '');
                        const lang = match ? match[1] : '';

                        if (!inline && lang === 'svg') {
                            const svgContent = String(children).replace(/\n$/, '');
                            return (
                                <div
                                    className={classes.svgContainer}
                                    dangerouslySetInnerHTML={{ __html: svgContent }}
                                />
                            );
                        }

                        return !inline ? (
                            <pre className={mergeClasses(classes.pre, isDarkMode ? classes.preDark : '')}>
                                <code className={className} {...props}>
                                    {children}
                                </code>
                            </pre>
                        ) : (
                            <code className={classes.code} {...props}>
                                {children}
                            </code>
                        );
                    },
                }}
            >
                {content}
            </ReactMarkdown>
        </div>
    );
};
